import { type QueryObserverResult, type RefetchOptions, type RefetchQueryFilters, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useRequestsContext } from "../../../contexts/RequestsContext";
import { useInvalidateCallIns } from "../../../hooks/useInvalidateCallIns";
import { useInvalidatePtoImpact } from "../../../hooks/useTimeOffImpact";
import { backendService } from "../../../services/backend.service";
import { trackErrorEvent, trackEvent } from "../../../utils/analytics";
import type {
	CalledOffShift,
	DeleteDayOffRequest,
	GetNurseNameExistsRequest,
	GetNurseProfileImage,
	GetNurseProfileRequest,
	Nurse,
	NurseInShift,
	NurseProfileToSubmit,
	SettableEmployeeStatus,
} from "../../../utils/models";

export function useGetNurseProfile(request: GetNurseProfileRequest) {
	const { showErrorSnackbar } = useHospitalData();
	const {
		data: profileData,
		isLoading: isLoadingProfile,
		error: errorProfile,
		isError: isErrorProfile,
		refetch: refetchProfile,
	} = useQuery<Nurse | null, Error>({
		queryKey: ["nurseProfile", request.department_id, request.nurse_id],
		queryFn: async () => {
			if (!request.nurse_id) {
				return null;
			}

			const { data } = await backendService.fetchNurseProfile(request.department_id, request.nurse_id);
			return data;
		},
		enabled: !!request.department_id && !!request.nurse_id, // avoid running if params are missing
		refetchOnWindowFocus: false,
		throwOnError: (error) => {
			const axiosError = error as AxiosError;
			const isNurseNotFound = axiosError.response?.status === 403 && (axiosError.response?.data as any)?.detail === "Nurse not exists in department";

			if (!isNurseNotFound) {
				showErrorSnackbar(
					"Failed to fetch nurse profile",
					{
						department_id: request.department_id,
						nurse_id: request.nurse_id,
					},
					error,
				);
			} else {
				trackErrorEvent("nurse_profile_fetch_error", error, {
					department_id: request.department_id,
					nurse_id: request.nurse_id,
				});
			}
			return false;
		},
		meta: {
			onSuccess: (data: Nurse | null) => {
				trackEvent("nurse_profile_fetched", {
					department_id: request.department_id,
					nurse_id: request.nurse_id,
					nurse_name: `${data?.firstName} ${data?.lastName}`,
				});
			},
		},
	});

	return {
		profileData,
		isLoadingProfile,
		isErrorProfile,
		errorProfile,
		refetchProfile,
	};
}

export function useGetNurseProfileImage(request: GetNurseProfileImage) {
	const { data: imageDataString } = useQuery<string | null, Error>({
		queryKey: ["nurseProfileImage", request.nurse_id],
		queryFn: async () => {
			try {
				const resData = (await backendService.fetchNurseProfileImage(request.nurse_id)).data;
				return `data:image/jpeg;base64,${resData}`;
			} catch (error) {
				//catching here to silently handle errors, so that the profile can still load without an image
				if (error instanceof AxiosError && error.response?.status !== 404) {
					// 404 is ok, other errors should be tracked
					trackErrorEvent("nurse_profile_image_fetch_error", error, {
						nurse_id: request.nurse_id,
					});
				}
				return null;
			}
		},
		enabled: !!request.nurse_id, // avoid running if params are missing
		refetchOnWindowFocus: false,
		retry: false,
		meta: {
			onSuccess: (data: string | undefined) => {
				trackEvent("nurse_profile_image_fetched", {
					nurse_id: request.nurse_id,
					image_size: data ? data.length : 0,
				});
			},
		},
	});

	return {
		imageDataString,
	};
}

export function useDeleteDayOff({
	addNewDBNurseShifts,
	removeNursesShiftsCompletely,
	refetchProfile,
}: {
	addNewDBNurseShifts: (shiftsToAdd: NurseInShift[]) => void;
	removeNursesShiftsCompletely: (shiftsToRemove: string[]) => void;
	refetchProfile?: (options?: (RefetchOptions & RefetchQueryFilters) | undefined) => Promise<QueryObserverResult<Nurse | null, Error>>;
}) {
	const { showErrorSnackbar } = useHospitalData();
	const invalidatePtoImpact = useInvalidatePtoImpact();

	const { mutateAsync: deleteDayOffMutateAsync, isPending } = useMutation({
		mutationFn: async ({ nurseId, departmentId, dayOff, restoreRecurringShifts, restoreTrimmedShifts }: DeleteDayOffRequest) => {
			const response = await backendService.deleteDayOffInProfile(nurseId, departmentId, dayOff.id, restoreRecurringShifts, restoreTrimmedShifts);
			return response.data as { addedShifts: NurseInShift[]; removedShiftIds: string[] };
		},
		onSuccess: async (data, variables) => {
			trackEvent("day_off_deleted", {
				nurse_id: variables.nurseId,
				day_off_id: variables.dayOff.id,
				date_from: variables.dayOff.dateFrom,
			});
			// Partial-shift PTO: drop the retired trim/split segments before adding the restored
			// originals, so local state doesn't show both the segments and the merged shift.
			removeNursesShiftsCompletely(data.removedShiftIds);
			addNewDBNurseShifts(data.addedShifts);
			await invalidatePtoImpact();
			await refetchProfile?.();
		},
		onError: (error, request) => {
			trackErrorEvent("day_off_delete_error", error, {
				nurse_id: request.nurseId,
				department_id: request.departmentId,
				day_off: request.dayOff,
			});
			showErrorSnackbar(
				"Failed to delete day off",
				{
					action: "failed to delete day off",
					nurse_id: request.nurseId,
					department_id: request.departmentId,
					day_off: request.dayOff,
				},
				error,
			);
		},
	});
	return {
		deleteDayOffMutateAsync,
		isPending,
	};
}

type EditOrAddNurseError = {
	detail: {
		error_type: string;
		message: string;
	};
};

type AddNurseProfileRequest = {
	nurseObjectToSubmit: NurseProfileToSubmit;
	shouldCreateUser: boolean;
	shouldNotify: boolean;
};

export type DuplicateExistingNurseMatch = {
	id: number;
	firstName: string;
	lastName: string;
	createdAt: string | null;
};

export function useCheckDuplicateNurse() {
	return useMutation({
		mutationFn: async ({ firstName, lastName, departmentId }: { firstName: string; lastName: string; departmentId: string }) => {
			const response = await backendService.checkDuplicateNurse(firstName, lastName, departmentId);
			return response.data.matchedNurses;
		},
	});
}

export function useAddNewNurseProfile() {
	const { showErrorSnackbar } = useHospitalData();
	const { mutateAsync: addNurseProfileAsync, isPending: isAddingNurseLoading } = useMutation({
		mutationFn: async ({ nurseObjectToSubmit, shouldCreateUser, shouldNotify }: AddNurseProfileRequest) => {
			const response = await backendService.postNewNurseProfile(nurseObjectToSubmit, shouldCreateUser, shouldNotify);
			return (response.data as Nurse).id;
		},
		onError: (error, request) => {
			const axiosError = error as AxiosError;
			const detailedError = axiosError.response?.data as EditOrAddNurseError;
			trackErrorEvent("nurse_creation_error", error, {
				nurseData: request.nurseObjectToSubmit,
				shouldCreateUser: request.shouldCreateUser,
				detail: detailedError?.detail,
			});
			showErrorSnackbar(
				detailedError?.detail.message
					? detailedError.detail.message
					: `Failed to create nurse profile ${detailedError.detail ? `with error: ${detailedError.detail}` : ""}`,
				{
					nurseData: request.nurseObjectToSubmit,
					shouldCreateUser: request.shouldCreateUser,
				},
				error,
			);
		},
		onSuccess: (data, request) => {
			trackEvent("nurse_profile_created", {
				nurse_id: data,
				nurse_data: request.nurseObjectToSubmit,
				should_create_user: request.shouldCreateUser,
			});
		},
	});

	return {
		addNurseProfileAsync,
		isAddingNurseLoading,
	};
}

type EditNurseProfileRequest = {
	nurseObjectToEdit: NurseProfileToSubmit;
};

export function useEditNurseProfile() {
	const { showErrorSnackbar, chosenDepartment } = useHospitalData();
	const queryClient = useQueryClient();
	const { mutateAsync: editNurseProfileAsync, isPending: isEditNurseLoading } = useMutation({
		mutationFn: async ({ nurseObjectToEdit }: EditNurseProfileRequest) => {
			const response = await backendService.putUpdatedNurseProfile(nurseObjectToEdit, chosenDepartment.department_id);
			return response.data as Nurse;
		},
		onError: (error, request) => {
			const axiosError = error as AxiosError;
			const detailedError = axiosError.response?.data as EditOrAddNurseError;
			trackErrorEvent("nurse_edit_error", error, {
				nurseData: request.nurseObjectToEdit,
				detail: detailedError?.detail,
			});
			showErrorSnackbar(
				detailedError ? detailedError.detail.message : "Failed to edit nurse profile",
				{
					nurseData: request.nurseObjectToEdit,
				},
				error,
			);
		},
		onSuccess: (data, request) => {
			trackEvent("nurse_profile_edited", {
				nurse_id: data.id,
				nurse_data: request.nurseObjectToEdit,
			});

			for (const key of ["predict-cost-batch", "predict-cost", "draft-day-stats"]) {
				queryClient.invalidateQueries({ queryKey: [key] });
			}
		},
	});

	return {
		editNurseProfileAsync,
		isEditNurseLoading,
	};
}

type PostProfileImageRequest = {
	nurseId: number;
	imageFile: File;
};

type DeactivateNurseRequest = {
	departmentId: string;
	nurseId: number;
	deactivationDate?: string;
};

export const useScheduleNurseDeactivation = () => {
	const { showErrorSnackbar, refreshDepartmentData } = useHospitalData();
	const { refreshRequests } = useRequestsContext();
	const { mutateAsync: scheduleDeactivation, isPending: isSchedulingDeactivation } = useMutation({
		mutationFn: async ({
			departmentId,
			nurseId,
			deactivationDate,
			postShifts,
		}: {
			departmentId: string;
			nurseId: number;
			deactivationDate: string;
			postShifts: boolean;
		}) => {
			const response = await backendService.scheduleNurseDeactivation(departmentId, nurseId, deactivationDate, postShifts);
			// Deactivation removes the nurse's future shifts server-side; without this the schedule grid keeps
			// showing them assigned and schedulers re-remove shifts the backend already deleted (DAT-712).
			await Promise.all([refreshRequests(), refreshDepartmentData(departmentId, false)]);
			return response.data;
		},
		onError: (error, request) => {
			trackErrorEvent("schedule_deactivation_error", error, {
				action: "failed to schedule nurse deactivation",
				nurse_id: request.nurseId,
			});
			const axiosError = error as AxiosError;
			const detailedError = axiosError.response?.data as EditOrAddNurseError;
			showErrorSnackbar(
				detailedError?.detail.message
					? detailedError.detail.message
					: `Failed to schedule nurse deactivation ${detailedError.detail ? `with error: ${detailedError.detail}` : ""}`,
				{
					nurseId: request.nurseId,
					departmentId: request.departmentId,
				},
				error,
			);
		},
		onSuccess: (data, request) => {
			trackEvent("nurse_schedule_deactivation_success", {
				nurse_id: request.nurseId,
			});
		},
	});

	return {
		scheduleDeactivation,
		isSchedulingDeactivation,
	};
};

export const useActivateNurse = () => {
	const { showErrorSnackbar, refreshDepartmentData } = useHospitalData();
	const { mutateAsync: activateNurse, isPending: isActivatingNurse } = useMutation({
		mutationFn: async ({ departmentId, nurseId }: DeactivateNurseRequest) => {
			const response = await backendService.activateNurse(departmentId, nurseId);
			// Reactivation is not a flag flip — it restores the recurring schedule the deactivation cascade
			// tore down, writing shifts back into the grid, so it needs the same refresh (DAT-712).
			await refreshDepartmentData(departmentId, false);
			return response.data;
		},
		onError: (error, request) => {
			trackErrorEvent("activate_nurse_error", error, {
				action: "failed to activate nurse",
				nurse_id: request.nurseId,
			});
			const axiosError = error as AxiosError;
			const detailedError = axiosError.response?.data as EditOrAddNurseError;
			showErrorSnackbar(
				detailedError?.detail.message
					? detailedError.detail.message
					: `Failed to activate nurse profile ${detailedError.detail ? `with error: ${detailedError.detail}` : ""}`,
				{
					nurseId: request.nurseId,
					departmentId: request.departmentId,
				},
				error,
			);
		},
		onSuccess: (data, request) => {
			trackEvent("nurse_activate_success", {
				nurse_id: request.nurseId,
			});
		},
	});

	return {
		activateNurse,
		isActivatingNurse,
	};
};

type SetEmployeeStatusRequest = {
	departmentId: string;
	nurseId: number;
	status: SettableEmployeeStatus;
	statusReason: string | null;
	startDate: string;
	endDate: string | null;
};

export const useSetEmployeeStatus = () => {
	const { t } = useTranslation();
	const { showErrorSnackbar, refreshNursesData, refreshDepartmentData } = useHospitalData();
	const { refreshRequests } = useRequestsContext();
	const { track, trackError } = useAnalytics();
	const { mutateAsync: setEmployeeStatus, isPending: isSettingEmployeeStatus } = useMutation({
		mutationFn: async ({ departmentId, nurseId, status, statusReason, startDate, endDate }: SetEmployeeStatusRequest) => {
			const response = await backendService.setEmployeeStatus(departmentId, nurseId, {
				status,
				status_reason: statusReason,
				start_date: startDate,
				end_date: endDate,
			});
			await refreshNursesData(departmentId);
			await refreshRequests();
			// A leave status tears down the nurse's shifts in the window, so the grid must reload too (DAT-712).
			await refreshDepartmentData(departmentId, false);
			return response.data;
		},
		onError: (error, request) => {
			trackError("set_employee_status_error", {
				error: error.message,
				action: "failed to set employee status",
				nurse_id: request.nurseId,
				status: request.status,
			});
			const axiosError = error as AxiosError;
			const detailedError = axiosError.response?.data as EditOrAddNurseError;
			showErrorSnackbar(
				detailedError?.detail?.message ?? t("employee.status.save.error"),
				{ nurseId: request.nurseId, departmentId: request.departmentId, status: request.status },
				error,
			);
		},
		onSuccess: (_data, request) => {
			track("set_employee_status_success", { nurse_id: request.nurseId, status: request.status });
		},
	});

	return { setEmployeeStatus, isSettingEmployeeStatus };
};

export const useClearEmployeeStatus = () => {
	const { t } = useTranslation();
	const { showErrorSnackbar, refreshNursesData, refreshDepartmentData } = useHospitalData();
	const { refreshRequests } = useRequestsContext();
	const { track, trackError } = useAnalytics();
	const { mutateAsync: clearEmployeeStatus, isPending: isClearingEmployeeStatus } = useMutation({
		mutationFn: async ({ departmentId, nurseId }: { departmentId: string; nurseId: number }) => {
			const response = await backendService.clearEmployeeStatus(departmentId, nurseId);
			// Clearing a leave status restores the shifts it removed, so the grid must reload here too (DAT-712).
			await Promise.all([refreshNursesData(departmentId), refreshRequests(), refreshDepartmentData(departmentId, false)]);
			return response.data;
		},
		onError: (error, request) => {
			trackError("clear_employee_status_error", {
				error: error.message,
				action: "failed to clear employee status",
				nurse_id: request.nurseId,
			});
			const axiosError = error as AxiosError;
			const detailedError = axiosError.response?.data as EditOrAddNurseError;
			showErrorSnackbar(
				detailedError?.detail?.message ?? t("employee.status.clear.error"),
				{ nurseId: request.nurseId, departmentId: request.departmentId },
				error,
			);
		},
		onSuccess: (_data, request) => {
			track("clear_employee_status_success", { nurse_id: request.nurseId });
		},
	});

	return { clearEmployeeStatus, isClearingEmployeeStatus };
};

export const useRemoveNurse = () => {
	const { showErrorSnackbar } = useHospitalData();
	const { mutateAsync: removeNurse, isPending: isRemovingNurse } = useMutation({
		mutationFn: async ({ departmentId, nurseId }: { departmentId: string; nurseId: number }) => {
			const response = await backendService.removeNurseFromDepartment(departmentId, nurseId);
			return response.data;
		},
		onError: (error, request) => {
			trackErrorEvent("remove_nurse_error", error, {
				action: "failed to remove nurse from department",
				nurse_id: request.nurseId,
				department_id: request.departmentId,
			});
			const axiosError = error as AxiosError;
			const detailedError = axiosError.response?.data as EditOrAddNurseError;
			showErrorSnackbar(
				detailedError?.detail?.message ? detailedError.detail.message : "Failed to remove nurse from department",
				{ nurseId: request.nurseId, departmentId: request.departmentId },
				error,
			);
		},
		onSuccess: (_data, request) => {
			trackEvent("nurse_removed_from_department_success", {
				nurse_id: request.nurseId,
				department_id: request.departmentId,
			});
		},
	});

	return { removeNurse, isRemovingNurse };
};

export function usePostProfileImage() {
	const { showErrorSnackbar } = useHospitalData();
	const { mutateAsync: postProfileImageAsync, isPending: isPostProfileImageLoading } = useMutation({
		mutationFn: async ({ imageFile, nurseId }: PostProfileImageRequest) => {
			const response = await backendService.postProfileImage(nurseId, imageFile);
			return response.data.file_url as string;
		},
		onError: (error, request) => {
			if (!request.nurseId) return;
			trackErrorEvent("nurse_profile_save_image_error", error, {
				action: "failed to save nurse profile image",
				nurse_id: request.nurseId,
			});
			showErrorSnackbar(
				"Failed to save nurse profile image",
				{
					action: "failed to save nurse profile image",
					nurse_id: request.nurseId,
				},
				error,
			);
		},
		onSuccess: (data, request) => {
			trackEvent("nurse_profile_image_saved", {
				nurse_id: request.nurseId,
				image_url: data,
			});
		},
	});

	return {
		postProfileImageAsync,
		isPostProfileImageLoading,
	};
}

export function useGetNurseNameExists(request: GetNurseNameExistsRequest) {
	return useQuery<{ exists: boolean; nurse_id: number | null }, Error>({
		queryKey: ["nurseNameExists", request.department_id, request.first_name, request.last_name],
		queryFn: async () => {
			if (!request.department_id || !request.first_name || !request.last_name) {
				return { exists: false, nurse_id: null };
			}
			return (await backendService.fetchNurseNameExistsV2(request.department_id, request.first_name, request.last_name, request.nurse_id)).data;
		},
		enabled: !!request.department_id && !!request.first_name && !!request.last_name && request.isProfileEditable,
		refetchOnWindowFocus: false,
		gcTime: 0, // replaces keepPreviousData: false
		throwOnError: (error) => {
			trackErrorEvent("nurse_name_exists_fetch_error", error, {
				department_id: request.department_id,
				first_name: request.first_name,
				last_name: request.last_name,
				nurse_id: request.nurse_id,
			});
			return false;
		},
		meta: {
			onSuccess: (data: { exists: boolean; nurse_id: number | null }) => {
				trackEvent("fetched_nurse_name_exists", {
					department_id: request.department_id,
					first_name: request.first_name,
					last_name: request.last_name,
				});
			},
		},
	});
}

export function useCancelCallOffs() {
	const { showErrorSnackbar, chosenDepartment } = useHospitalData();
	const { track, trackError } = useAnalytics();
	const invalidateCallIns = useInvalidateCallIns();
	return useMutation({
		mutationFn: async (callOffs: CalledOffShift[]) => {
			track("cancel_nurse_call_offs_attempt", { department_id: chosenDepartment.department_id, callOffs: JSON.stringify(callOffs) });
			const response = await backendService.cancelNurseCallOffs({
				calledOffShifts: callOffs,
				departmentId: chosenDepartment.department_id,
			});
			return response.data;
		},
		// The mirror of the removal invalidation: undoing a call-in has to clear the calendar marker and
		// drop the mobile card too. `useLastCallInsState` also patches the row locally for instant
		// feedback — the refetch that follows just confirms it against the server.
		onSuccess: () => {
			void invalidateCallIns();
		},
		throwOnError: false,
		onError: (error, variables) => {
			trackError("cancel_nurse_call_offs_error", {
				error: error.message,
				department_id: chosenDepartment.department_id,
				callOffs: JSON.stringify(variables),
			});
			showErrorSnackbar("Failed to cancel nurse call-offs", { department_id: chosenDepartment.department_id }, error);
		},
	});
}

const NURSE_UNIONS_QUERY_KEY = "departmentNurseUnions";
export function useGetDepartmentNurseUnions() {
	const { chosenDepartment, showErrorSnackbar } = useHospitalData();
	const { track } = useAnalytics();
	return useQuery<string[], Error>({
		queryKey: [NURSE_UNIONS_QUERY_KEY, chosenDepartment.department_id],
		queryFn: async () => {
			track("fetch_department_nurse_unions", { department_id: chosenDepartment.department_id });
			const res = await backendService.getDepartmentNurseUnions(chosenDepartment.department_id);
			track("fetched_department_nurse_unions_success", { department_id: chosenDepartment.department_id });
			return res.data;
		},
		throwOnError: (error) => {
			showErrorSnackbar("Failed to fetch nurse unions", { department_id: chosenDepartment.department_id }, error);
			return false;
		},
		refetchOnWindowFocus: true,
		refetchOnMount: true,
		staleTime: 1000 * 30, // 30 seconds
	});
}
